import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "wouter";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CalendarDays, MapPin, Music, Users, Target, Heart, Sprout, TrendingUp, Award, Crown, Mic2, X, Check, Radio } from "lucide-react";
import { formatDate } from "@/modules/calculator";
import type { ArtistWithShows } from "@shared/schema";
import type { ArtistWithMatch } from "@/lib/audience-matching";
import { filterShowsByAudienceMatch } from "@/lib/audience-matching";
import { normalizeCareerStageDisplay } from "@/modules/calculator";
import { getMatchBadgeClasses, getMatchBorderColor, getMatchBadgeBackground, getMatchBadgeHoverBackground } from '@/lib/match-colors';
import { trimShowsByFilters, type ShowFilterConfig } from '@shared/filter-engine';
import { BrowseFilters } from '@/modules/browse/types/browse.types';

interface ArtistCardProps {
  artist: ArtistWithShows | ArtistWithMatch;
  onViewArtist: (artist: ArtistWithShows | ArtistWithMatch) => void;
  onSaveAllShows?: (artist: ArtistWithShows | ArtistWithMatch) => void;
  onUnsaveAllShows?: (artist: ArtistWithShows | ArtistWithMatch) => void;
  onSaveArtist?: (artist: ArtistWithShows | ArtistWithMatch) => void;
  isAuthenticated?: boolean;
  isShowSaved?: (showId: number) => boolean;
  isArtistSaved?: (artistId: number) => boolean;
  currentFilters?: BrowseFilters;
}

export default function ArtistCard({ artist, onViewArtist, onSaveAllShows, onUnsaveAllShows, onSaveArtist, isAuthenticated = false, isShowSaved, isArtistSaved, currentFilters }: ArtistCardProps) {
  const [showMatchDetails, setShowMatchDetails] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0, showBelow: false });
  const badgeRef = useRef<HTMLDivElement>(null);
  const hasSetProfile = !!artist.setArtistGroupId;
  
  // Use shared UTC-safe formatter for date strings/objects

  const getImageSrc = () => {
    return artist.imageUrl || "https://max.live/hubfs/2Crop-Cell-phone-concert-copy.jpg";
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    if (img.src !== "https://max.live/hubfs/2Crop-Cell-phone-concert-copy.jpg") {
      img.src = "https://max.live/hubfs/2Crop-Cell-phone-concert-copy.jpg";
    }
  };

  const isArtistWithMatch = (artist: ArtistWithShows | ArtistWithMatch): artist is ArtistWithMatch => {
    return 'audienceMatchPercentage' in artist;
  };

  const updatePopupPosition = () => {
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      
      // Simple positioning: above the badge if possible, below if not
      let left = rect.left;
      let top = rect.top - 8; // Start above badge
      let showBelow = false;
      
      // If popup would go above the top of screen, position below
      if (top - 320 < 20) { // 320px max height + 20px buffer
        top = rect.bottom + 8;
        showBelow = true;
      }
      
      // Keep popup on screen horizontally
      if (left + 384 > window.innerWidth) { // 384px width
        left = window.innerWidth - 384 - 16;
      }
      if (left < 16) {
        left = 16;
      }
      
      setPopupPosition({ top, left, showBelow });
    }
  };

  const handleToggleMatchDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!showMatchDetails) {
      updatePopupPosition();
    }
    setShowMatchDetails(!showMatchDetails);
  };

  const getCareerStageIcon = (careerStage: string | null) => {
    switch (careerStage?.toLowerCase()) {
      case 'emerging':
        return <Sprout className="w-4 h-4" />;
      case 'breakthrough':
        return <TrendingUp className="w-4 h-4" />;
      case 'established':
        return <Award className="w-4 h-4" />;
      case 'mainstream':
        return <Crown className="w-4 h-4" />;
      default:
        return <Radio className="w-4 h-4" />;
    }
  };

  // Filter shows based on current filters to get accurate count
  const filteredShowCount = (() => {
    const shows = artist.upcomingShows || [];
    if (!currentFilters || shows.length === 0) {
      return artist.totalShows || shows.length || 0;
    }
    const showConfig: ShowFilterConfig = {
      dateFrom: currentFilters.dateFrom,
      dateTo: currentFilters.dateTo,
      genre: currentFilters.genre,
      location: currentFilters.location,
      artistName: currentFilters.artistName,
      venueName: currentFilters.venueName,
    };
    let filtered = trimShowsByFilters(shows, showConfig, { excludePastShows: false });
    filtered = filterShowsByAudienceMatch(filtered, currentFilters.targetAudience, currentFilters.minMatchPercentage);
    return filtered.length;
  })();

  const getGenreColor = (genre: string) => {
    const colors = {
      "Pop": "bg-pink-100 text-pink-800",
      "Hip-Hop": "bg-indigo-100 text-indigo-800",
      "Rock": "bg-green-100 text-green-800",
      "R&B": "bg-purple-100 text-purple-800",
      "Alternative": "bg-yellow-100 text-yellow-800",
      "Country": "bg-orange-100 text-orange-800",
      "Electronic": "bg-blue-100 text-blue-800",
      "Folk": "bg-emerald-100 text-emerald-800",
      "Reggaeton": "bg-red-100 text-red-800",
      "Latin": "bg-amber-100 text-amber-800",
      "Dance": "bg-cyan-100 text-cyan-800",
    };
    return colors[genre as keyof typeof colors] || "bg-gray-100 text-gray-800";
  };

  return (
    <>
      <Link href={`/artist/${artist.id}`} onClick={(e) => {
        // Preserve filters in URL for back navigation, but artist detail page won't filter shows
        e.preventDefault();
        const params = new URLSearchParams(window.location.search);
        const queryString = params.toString();
        window.location.href = `/artist/${artist.id}${queryString ? `?${queryString}` : ''}`;
      }}>
        <div className="relative aspect-square rounded-lg overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ease-out cursor-pointer touch-manipulation active:scale-[0.98] group will-change-transform" style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}>
          {/* Full-bleed artist image */}
          <img
            src={getImageSrc()}
            alt={`${artist.name} - Artist photo`}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={handleImageError}
          />
        
          {/* Save heart button - top right */}
          {isAuthenticated && onSaveArtist && isArtistSaved && (
            <button
              className={`absolute top-4 right-4 p-2 rounded-full backdrop-blur-md border transition-all duration-200 hover:scale-110 active:scale-95 z-20 group/heart ${
                isArtistSaved(artist.id)
                  ? 'bg-[#FA017C]/20 border-[#FA017C]/40 hover:bg-[#FA017C]/30'
                  : 'bg-black/20 border-white/20 hover:bg-[#FA017C]/20'
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSaveArtist(artist);
              }}
              aria-label={isArtistSaved(artist.id) ? `Remove ${artist.name} from saved artists` : `Save ${artist.name} to saved artists`}
            >
              <Heart 
                className={`w-4 h-4 transition-all duration-200 ${
                  isArtistSaved(artist.id) 
                    ? "fill-[#FA017C] text-[#FA017C]" 
                    : "text-white/80 group-hover/heart:text-[#FA017C] group-hover/heart:fill-[#FA017C]"
                }`} 
              />
            </button>
          )}

          {/* Audience match badge - top left */}
          {isArtistWithMatch(artist) && (
            <div className="absolute top-4 left-4 z-20">
              <Badge 
                ref={badgeRef}
                className={`backdrop-blur-md border text-white cursor-pointer transition-all duration-200 ${getMatchBorderColor(artist.audienceMatchPercentage)} ${getMatchBadgeBackground(artist.audienceMatchPercentage)}`}
                style={{ 
                  backgroundColor: `rgb(0 0 0 / 0.3)`,
                }}
                onMouseEnter={(e) => {
                  const target = e.currentTarget;
                  const percentage = artist.audienceMatchPercentage;
                  if (percentage >= 75) {
                    target.style.backgroundColor = 'rgb(34 197 94 / 0.2)'; // green-500/20
                  } else if (percentage >= 40) {
                    target.style.backgroundColor = 'rgb(234 179 8 / 0.2)'; // yellow-500/20
                  } else {
                    target.style.backgroundColor = 'rgb(239 68 68 / 0.2)'; // red-500/20
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgb(0 0 0 / 0.3)';
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleToggleMatchDetails(e);
                }}
              >
                <Target className="w-3 h-3 mr-1" />
                {artist.audienceMatchPercentage}% Match
              </Badge>
            </div>
          )}

          {/* Dark glassmorphism overlay for content - bottom third */}
          <div className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-md border-t border-white/10 p-3 space-y-2">
            {/* Artist name with show count */}
            <div className="flex items-center justify-between gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h3 className="text-lg font-bold text-white truncate flex-1 min-w-0">
                      {artist.name}
                    </h3>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p>{artist.name}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* SET icon — shown when artist has a SET profile */}
              {hasSetProfile && (
                <img src="/set.ico" alt="SET" className="w-5 h-5 rounded-sm flex-shrink-0" />
              )}
              <div className="flex items-center gap-1 text-white/90 flex-shrink-0">
                <Mic2 className="w-4 h-4 text-white/70" />
                <span className="text-base font-semibold">{filteredShowCount}</span>
              </div>
            </div>

            {/* Metadata with icons and View Artist button */}
            <div className="flex items-end justify-between gap-3">
              <div className="space-y-1 flex-1 min-w-0 overflow-hidden">
                {/* Genre */}
                {artist.genres && artist.genres.length > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5 text-xs text-white/90 min-w-0">
                          <Music className="w-3 h-3 text-white/70 flex-shrink-0" />
                          <span className="truncate">{artist.genres.slice(0, 2).join(', ')}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p>{artist.genres.join(', ')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {/* Career Stage */}
                {artist.careerStage && (
                  <div className="flex items-center gap-1.5 text-xs text-white/90 min-w-0">
                    {(() => {
                      const icon = getCareerStageIcon(artist.careerStage);
                      return React.cloneElement(icon as React.ReactElement, {
                        className: "w-3 h-3 text-white/70 flex-shrink-0"
                      });
                    })()}
                    <span className="truncate">{normalizeCareerStageDisplay(artist.careerStage)}</span>
                  </div>
                )}
              </div>

              {/* View Artist CTA Button */}
              <div className="flex-shrink-0">
                <div className="px-3 py-1.5 border border-[#FA017C] bg-white/10 backdrop-blur-sm text-white hover:bg-[#FA017C]/20 hover:border-[#FA017C] transition-all duration-200 text-xs rounded-md font-medium whitespace-nowrap">
                  View Artist →
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
      
      {/* Audience Match Details Popup - keep existing popup logic outside Link */}
      {isArtistWithMatch(artist) && showMatchDetails && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowMatchDetails(false)}
          />
          
          {/* Popup */}
          <div 
            className="fixed z-50 w-96 p-4 bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-y-auto"
            style={{ 
              top: `${popupPosition.top}px`, 
              left: `${popupPosition.left}px`,
              transform: popupPosition.showBelow ? 'translateY(0)' : 'translateY(-100%)'
            }}
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm">Audience Match Details</h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 w-6 p-0"
                  onClick={() => setShowMatchDetails(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Matched Attributes Header */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Matched Attributes ({artist.matchedCriteria}/{artist.totalCriteria})
                  </span>
                </div>
                {/* All Attributes - Matched (green) and Unmatched (red) */}
                <div className="flex flex-wrap gap-1">
                  {artist.matchingAttributes.map((attr, index) => (
                    <Badge key={`match-${index}`} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                      {attr}
                    </Badge>
                  ))}
                  {(artist as any).nonMatchingAttributes?.map((attr: string, index: number) => (
                    <Badge key={`nomatch-${index}`} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                      {attr}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>, 
        document.body
      )}
    </>
  );
}
